/**
 * Die Mieterauswahl (Spec 03, „Sheet Vertrag anlegen · Die Mieterauswahl").
 * Geteilt zwischen `ContractCreateSheet` (Feld `contract-create-tenant`, mit
 * „Neuen Mieter anlegen") und `ContractMasterData` (Feld `contract-edit-tenant`,
 * ohne — dort ändert man nur den bestehenden Mieter, siehe Spec 03,
 * „Reiter Stammdaten eines Vertrags": „Auswahl wie im Sheet").
 *
 * Ist bereits ein Mieter gewählt, zeigt das Feld ihn als Zeile mit „Ändern";
 * erst ein Klick darauf öffnet die Suche — genau wie `MoveSheet`s Zielsuche,
 * nur mit einem vorbelegbaren Ausgangszustand. Sucht über `GET /tenants?q=`
 * und lädt nicht vor, solange kein Suchtext steht (dieselbe Zurückhaltung wie
 * bei `MoveSheet`).
 */

import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { Ref } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { components } from '@/lib/api-types'
import { useTenantOptions } from '@/features/contracts/api'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

export interface TenantSelection {
  readonly id: string
  readonly label: string
  readonly salutation: components['schemas']['LabeledValue'] | null
}

interface TenantPickerProps {
  /** `contract-create-tenant` oder `contract-edit-tenant`. */
  readonly testId: string
  readonly value: TenantSelection | null
  readonly onSelect: (tenant: TenantSelection) => void
  /** Nur im Anlegen-Sheet gesetzt — am Vertrag selbst ändert man nur den
   * Mieter, legt aber keinen neuen aus diesem Feld heraus an. */
  readonly onRequestNewTenant?: () => void
  /** Nur in `ContractMasterData` gesetzt: „die Beschriftung ist zugleich Link
   * `contract-tenant-link` auf den Mieter" (Spec 03, „Reiter Stammdaten eines
   * Vertrags"). Im Anlegen-Sheet gibt es noch keinen Vertrag, zu dessen
   * Mieter man zurückspringen könnte. */
  readonly linkToTenant?: boolean
  readonly error: FieldError | undefined
  readonly errorPrefix: string
  readonly disabled?: boolean
  readonly searchRef?: Ref<HTMLInputElement>
}

const API_FIELD = 'tenant_id'

export function TenantPicker({
  testId,
  value,
  onSelect,
  onRequestNewTenant,
  linkToTenant = false,
  error,
  errorPrefix,
  disabled = false,
  searchRef,
}: TenantPickerProps) {
  const [q, setQ] = useState('')
  const [isSearching, setIsSearching] = useState(value === null)
  // Ein von außen gesetzter Mieter (z. B. der neu angelegte aus dem
  // Mietersheet, `ContractCreateSheet`s `pendingTenant`) schließt die Suche —
  // während des Renderns verglichen (React-Muster „Adjusting state when a
  // prop changes"), nicht in einem Effekt.
  const [syncedValueId, setSyncedValueId] = useState(value?.id)
  if (value?.id !== syncedValueId) {
    setSyncedValueId(value?.id)
    setIsSearching(value === null)
    setQ('')
  }
  const optionsQuery = useTenantOptions(q, { enabled: isSearching })
  const hasError = error?.field === API_FIELD
  const errorTestId = `${errorPrefix}-error-${API_FIELD}`

  function selectTenant(tenant: TenantSelection) {
    onSelect(tenant)
    setIsSearching(false)
    setQ('')
  }

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-label text-muted">Mieter</span>

      {value !== null && !isSearching ? (
        <div className={`flex items-center justify-between gap-2 ${FIELD_CLASS}`}>
          <span className="flex flex-col">
            {linkToTenant ? (
              <Link
                to="/objekt/$entityId"
                params={{ entityId: value.id }}
                search={{ tab: undefined }}
                data-testid="contract-tenant-link"
                // `min-h`/`min-w` in beide Richtungen — dieselbe Regel und
                // derselbe Fund wie bei `ObjectPath.tsx`s Pfadglied (Spec
                // 02a): ein kurzer Name ergibt sonst ein Ziel unter 44 px.
                // Gefunden von `assertTapTargets` in der Journey 03, nicht
                // im Entwurf.
                className="text-accent inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center hover:underline"
              >
                {value.label}
              </Link>
            ) : (
              <span className="text-text">{value.label}</span>
            )}
            {value.salutation !== null && <span className="text-label text-muted">{value.salutation.label}</span>}
          </span>
          {!disabled && (
            <button
              type="button"
              data-testid={`${testId}-change`}
              onClick={() => {
                setIsSearching(true)
              }}
              // Dieselbe Begründung wie bei `contract-tenant-link` oben.
              className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
            >
              Ändern
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            ref={searchRef}
            data-testid="contract-tenant-search"
            type="search"
            aria-label="Mieter suchen"
            placeholder="Mieter suchen"
            disabled={disabled}
            value={q}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorTestId : undefined}
            onChange={(event) => {
              setQ(event.target.value)
            }}
            className={`text-text ${FIELD_CLASS}`}
          />

          {q.trim() !== '' && optionsQuery.isPending && <SkeletonRows testId="contract-tenant-loading" rows={3} />}

          {q.trim() !== '' && optionsQuery.isError && (
            <ErrorState
              testId="contract-tenant-error"
              retryTestId="contract-tenant-retry"
              error={optionsQuery.error}
              onRetry={() => {
                void optionsQuery.refetch()
              }}
            />
          )}

          {q.trim() !== '' && optionsQuery.isSuccess && optionsQuery.data.items.length === 0 && (
            <EmptyState testId="contract-tenant-empty" title="Kein Mieter gefunden." />
          )}

          {q.trim() !== '' && optionsQuery.isSuccess && optionsQuery.data.items.length > 0 && (
            <ul className="border-border rounded-card border">
              {optionsQuery.data.items.map((tenant) => (
                <li key={tenant.id} className="border-border border-b last:border-b-0">
                  <button
                    type="button"
                    data-testid={`contract-tenant-option-${tenant.id}`}
                    onClick={() => {
                      selectTenant({ id: tenant.id, label: tenant.label, salutation: tenant.salutation })
                    }}
                    className="hover:bg-bg flex min-h-(--tap-min) w-full flex-col items-start justify-center gap-0.5 px-3 py-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                  >
                    <span className="text-body text-text">{tenant.label}</span>
                    {tenant.salutation !== null && (
                      <span className="text-label text-muted">{tenant.salutation.label}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {onRequestNewTenant && (
            <button
              type="button"
              data-testid="contract-create-tenant-new"
              onClick={onRequestNewTenant}
              className="text-accent text-label self-start hover:underline"
            >
              Neuen Mieter anlegen
            </button>
          )}
        </>
      )}

      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
